// Libraries
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Editor} from '@tinymce/tinymce-react';
import {Popover, PopoverBody} from 'reactstrap';
import classnames from 'classnames';
import produce from 'immer';
import PropTypes from 'prop-types';

// Components
import DropDown from 'Components/editor/components/DropDown';

// Assets
import './styles.scss';
import styles from './editor.module.scss';
import useDebounce from '../../hooks/useDebounce';
import {getObjectPropSafely, random} from '../../utils';

// Icons
import 'Components/icon/icons.scss';

const defaultProps = {
    onChange: () => 0,
    onError: () => 0,
    htmlText: '',
    personalizationType: [
        {
            value: 'userProperties.computedProperties',
            label: 'Visitor Attribute',
            color: '#009000'
        },
        {
            value: 'userProperties.customerProperties',
            label: 'Customer Attribute',
            color: '#3C39BA'
        },
        {
            value: 'eventProperties',
            label: 'Event Attribute',
            color: '#FBAC00'
        }
    ],
    personalizationData: {
        'userProperties.computedProperties': [
            {
                value: 'visitorId',
                bracketed_code: '(visitorId)',
                label: 'Visitor Id'
            },
            {
                value: 'ctime',
                bracketed_code: '(ctime)',
                label: 'Create time'
            },
            {
                value: 'utime',
                bracketed_code: '(utime)',
                label: 'Update time'
            }
        ],
        'userProperties.customerProperties': [
            {
                value: 'customerId',
                bracketed_code: '(customerId)',
                label: 'Customer Id'
            },
            {
                value: 'customerName',
                bracketed_code: '(customerName)',
                label: 'Customer Name'
            },
            {
                value: 'customerAddress',
                bracketed_code: '(customerAddress)',
                label: 'Customer address'
            }
        ],
        'eventProperties': [
            {
                value: 'eventId',
                bracketed_code: '[eventId]',
                label: 'Event Id'
            },
            {
                value: 'eventName',
                bracketed_code: '[eventName]',
                label: 'Event Name'
            },
            {
                value: 'eventCategory',
                bracketed_code: '[eventCategory]',
                label: 'Event Category'
            }
        ]
    },
    menubar: false,
    plugins: ['advlist autolink lists link image charmap print preview anchor',
        'searchreplace visualblocks code fullscreen',
        'insertdatetime media paste code help wordcount casechange'],
    toolbar: 'formatselect | personalTag | bold italic underline strikethrough code|\
                         forecolor backcolor preview link |\
                        alignleft aligncenter alignright alignjustify outdent indent |\
                        numlist bullist checklist undo redo',
    minHeight: 330,
    maxHeight: 800,
    maxWidth: 800,
    minWidth: 330
};

const PATH = 'src/modules/Workflow/components/SidePanel/components/TinyMCEEditor/index.jsx';

const getInitialSelected = (props) => {
    let personalType = {};
    let attribute = {};
    let value = '';

    try {
        if (props.personalizationType && props.personalizationType.length) {
            personalType = {
                value: props.personalizationType[0].value,
                label: props.personalizationType[0].label
            };

            if (getObjectPropSafely(() => (props.personalizationData.hasOwnProperty(props.personalizationType[0].value))))
            {
                attribute = getObjectPropSafely(() => props.personalizationData[props.personalizationType[0].value][0],{});
            }
        }
    } catch (error) {
        typeof props.onError === 'function' && props.onError(error, {
            component: PATH,
            action: 'getInitialSelected',
            args: {props}
        });
    }

    return {
        personalType,
        attribute,
        value
    };
};

const getInsertPattern = (id, code, label, bgColor) => {

    return `&nbsp;<button id=${id} class="insert-word" contentEditable="false" style="padding: 5px 8px; color: white; font-size: inherit; outline: none;border: 1px solid transparent; border-radius: 1.2rem; min-height: 30px; cursor: pointer; background: ${bgColor}" value=${code.replace(/\s/gm,'&nbsp;')}>&nbsp;${label}&nbsp;<em class="editor-icon-remove" style="color: white;font-style: normal;cursor: pointer;outline: none;">x</em></button>&nbsp;`;
};

export const convertHtmlText = (htmlText, format = 'raw') => {
    let result = htmlText;

    try {
        if (format && format.toLowerCase() === 'raw') {
            // pattern find all button insert word
            const regexInsertPattern = /<button .*?class="insert-word".*? value="?(.*?)"?>(.*?)<\/button>/gmi;
            // pattern find all symbol `"`
            const regexQuot = /&quot;/gm;
            const regexSpace = /&nbsp;/gm;

            const replacer = (match, p1, p2, p3, offset, string) => {
                return p1.replace(regexQuot,'"').replace(regexSpace,' ');
            };

            try {
                return result.replace(regexQuot,'"').replace(regexSpace,' ').replace(regexInsertPattern, replacer);
            } catch (error) {
                return htmlText;
            }
        }

        if (format && format.toLowerCase() === 'html') {
            const insertPattern = /#{.*?}/gm;

            if (insertPattern.test(htmlText)) {
                const htmlReplacer = (matcher) => {
                    // const regex = /(\w+.\w+)|(\(\w+)\)|"(.*?)"/gmi;
                    const regex = /(\w+.\w+)|(\(\w+)\)|"(.*?)"/gmi;
                    const found = matcher.match(regex);

                    if (found && found.length === 3) {
                        let bgColor = '#009000';

                        if (found[0] === 'userProperties.customerProperties') {bgColor = '#3C39BA'}
                        if (found[0] === 'eventProperties') {bgColor = '#FBAC00'}

                        const label = getObjectPropSafely(() => {
                            const foundAttribute = defaultProps.personalizationData[found[0]].find(item => item.bracketed_code === found[1]);

                            if (foundAttribute) {return foundAttribute.label}

                            return '';

                        });

                        return getInsertPattern(random(8),matcher,label,bgColor);

                    }
                    return matcher;
                };

                try {
                    return result.replace(insertPattern, htmlReplacer);
                } catch (error) {
                    return htmlText;
                }

            }

        }
    } catch (error) {
        //
    }

    return result;
};

const modifiers = {
    preventOverflow: {
        enabled: false
    },
    flip: {
        enabled: true
    },
    hide: {
        enabled: false
    }
};

export const TinymceEditor = (props) => {
    const {htmlText = '', personalizationType = [], personalizationData = {}} = props;

    const [textValue, setTextValue] = useState(convertHtmlText(htmlText,'html'));
    // const [textRaw, setTextRaw] = useState(convertHtmlText(htmlText));
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [selectedProperties, setSelectedProperties] = useState(getInitialSelected(props));

    const debounceText = useDebounce(textValue,500);
    const wrapperRef = useRef(null);
    const editorRef = useRef(null);
    const myTagRef = useRef({});
    const currentTagActive = useRef('');

    // useEffect(() => {
    //     try {
    //         if (htmlText && convertHtmlText(htmlText) !== textValue) {
    //             setTextValue(convertHtmlText(htmlText));
    //         }
    //     } catch (error) {
    //         typeof props.onError === 'function' && props.onError(error, {
    //             component: PATH,
    //             action: 'useEffect',
    //             args: {}
    //         });
    //     }
    // },[htmlText]);

    useEffect(() => {
        try {
            if (typeof props.onChange === 'function') {
                props.onChange({
                    htmlText: convertHtmlText(textValue,'raw')
                    // htmlRaw: textRaw
                });
            }
        } catch (error) {
            typeof props.onError === 'function' && props.onError(error, {
                component: PATH,
                action: 'useEffect',
                args: {}
            });
        }
    },[debounceText]);

    const handleEditorChange = (content, editor) => {
        try {
            const contentRaw = convertHtmlText(editor.getContent({format: 'html'}));

            // setTextRaw(contentRaw);
            setTextValue(content);
        } catch (error) {
            typeof props.onError === 'function' && props.onError(error, {
                component: PATH,
                action: 'handleEditorChange',
                args: {debounceText}
            });
        }
    };

    const togglePopover = () => {
        setPopoverOpen(!popoverOpen);
        setSelectedProperties(getInitialSelected(props));
        currentTagActive.current = '';
    };

    const handleInsertText = () => {
        try {
            const id = random(5);
            let bgColor = '#009000';

            classNameTag === 'blue' && (bgColor = '#3C39BA');
            classNameTag === 'orange' && (bgColor = '#FBAC00');

            if (currentTagActive.current) {
                try {
                    editorRef.current.selection.setCursorLocation(editorRef.current.dom.get(currentTagActive.current), 0);
                    editorRef.current.dom.remove(`${currentTagActive.current}`);
                    currentTagActive.current = '';
                } catch (error) {
                    if (typeof props.onError === 'function') {
                        props.onError(error, {
                            component: PATH,
                            action: '',
                            args: {}
                        });
                    }
                }
            }
            editorRef.current.insertContent(getInsertPattern(id, personalizationCode, getObjectPropSafely(() => selectedProperties.attribute.label), bgColor));

            myTagRef.current = {
                ...myTagRef.current,
                [id]: selectedProperties
            };
            currentTagActive.current = '';
            setPopoverOpen(false);
            setSelectedProperties(getInitialSelected(props));
        } catch (error) {
            if (typeof props.onError === 'function') {
                props.onError(error, {
                    component: PATH,
                    action: '',
                    args: {}
                });
            }
        }
    };

    const editText = (event) => {
        try {
            if (event.target.id) {
                const value = event.target.value;
                const regex = /(\w+.\w+)|(\(\w+)\)|"(.*?)"/gmi;
                const found = value.match(regex);
                let personalType;
                let attributeType;
                let valueString = '';
                let typeItem;

                if (found && found.length) {
                    for (let index in found) {
                        const properties = found[+index].replace(/[{}"]/gm, '');

                        if (+index === 0) {
                            typeItem = props.personalizationType.find(item => item.value === properties);
                            if (typeItem) {
                                personalType = {
                                    value: typeItem.value,
                                    label: typeItem.label
                                };
                            }
                        }
                        if (+index === 1 && typeItem.value && props.personalizationData
                            && props.personalizationData.hasOwnProperty(typeItem.value)) {
                            const attribute = props.personalizationData[typeItem.value].find(item => item.bracketed_code === properties);

                            attribute && (
                                attributeType = attribute
                            );
                        }

                        if (+index === 2) {
                            valueString = properties || '';
                        }
                    }
                }

                if (personalType && attributeType) {
                    setSelectedProperties({
                        personalType,
                        attribute: attributeType,
                        value: valueString
                    });
                } else {
                    setSelectedProperties(getInitialSelected(props));
                }
                currentTagActive.current = event.target.id;
                setPopoverOpen(true);
            }
        } catch (error) {
            if (typeof props.onError === 'function') {
                props.onError(error, {
                    component: PATH,
                    action: '',
                    args: {}
                });
            }
        }
    };

    const onChangePersonalType = (option) => {
        try {
            const foundItem = getObjectPropSafely(() => {
                if (option && typeof option.value !== 'undefined' &&
                    props.personalizationData
                    && props.personalizationData.hasOwnProperty(option.value)) {
                    return props.personalizationData[option.value];
                }
            },[]);

            if (option && foundItem) {
                setSelectedProperties(produce(selectedProperties, (draft) => {
                    draft.personalType = {
                        value: option.value,
                        label: option.label
                    };

                    if (foundItem && foundItem.length) {
                        draft.attribute = {
                            value: foundItem[0].value,
                            label: foundItem[0].label,
                            bracketed_code: foundItem[0].bracketed_code
                        };
                    } else {
                        draft.attribute = {
                            value: '',
                            label: '',
                            bracketed_code: ''
                        };
                    }
                }));
            }
        } catch (error) {
            if (typeof props.onError === 'function') {
                props.onError(error, {
                    component: PATH,
                    action: '',
                    args: {}
                });
            }
        }
    };

    const onChangeAttribute = (option) => {
        try {
            if (option) {
                setSelectedProperties(produce(selectedProperties, (draft) => {
                    draft.attribute = option;
                }));
            }
        } catch (error) {
            if (typeof props.onError === 'function') {
                props.onError(error, {
                    component: PATH,
                    action: '',
                    args: {}
                });
            }
        }
    };

    const onChangeInputValue = (value) => {
        try {
            setSelectedProperties(produce(selectedProperties, (draft) => {
                draft.value = value;
            }));
        } catch (error) {
            if (typeof props.onError === 'function') {
                props.onError(error, {
                    component: PATH,
                    action: '',
                    args: {}
                });
            }
        }
    };

    const {sourceAttribute, classNameTag = ''} = useMemo(() => {
        let result = [];
        let className = '';

        result = getObjectPropSafely(() => {
            if (selectedProperties.personalType && selectedProperties.personalType.value
                && props.personalizationData
                && props.personalizationData.hasOwnProperty(selectedProperties.personalType.value)) {
                return props.personalizationData[selectedProperties.personalType.value];
            }
            return [];
        }, []);

        if (getObjectPropSafely(() => selectedProperties.personalType.label === 'Customer Attribute')) {
            className = 'blue';
        }

        if (getObjectPropSafely(() => selectedProperties.personalType.label === 'Event Attribute')) {
            className = 'orange';
        }

        {return {sourceAttribute: result, classNameTag: className}}
    },[selectedProperties.personalType]);

    const personalizationCode = useMemo(() => {
        return `#{${getObjectPropSafely(() => selectedProperties.personalType.value)}.${getObjectPropSafely(() => selectedProperties.attribute.bracketed_code)}||"${getObjectPropSafely(() => selectedProperties.value)}"}`;
    },[selectedProperties]);

    return (
        <div className={'ants-tinymce-editor'} style={{position: 'relative', maxWidth: '100% !important'}} ref={wrapperRef}>
            <Editor
                // apiKey={'pj3be4r7csm282qh8gsssq1cywkvjh2j7iic281901rof8aw'}
                // tinymceScriptSrc='./tinymce.min.js'
                initialValue={textValue}
                init={{
                    min_height: props.minHeight,
                    max_height: props.maxHeight,
                    max_width: props.maxWidth,
                    min_width: props.minWidth,
                    menubar: props.menubar || false,
                    plugins: props.plugins || [],
                    toolbar: props.toolbar,
                    skin: 'snow',
                    toolbar_mode: 'sliding',
                    branding: false,
                    body_class: 'antsomi-editor-body',
                    setup: function(editor) {
                        editor.on('init', function(e) {
                            editor.getBody().style.fontSize = '12px';
                            editorRef.current = editor;
                        });

                        editor.ui.registry.addButton('personalTag', {
                            text: '<span class="icon-antsomi-profile" style="font-size: 15px"></span>',
                            onAction: function(_) {
                                setPopoverOpen(true);
                            }
                        });
                    },
                    init_instance_callback: function(editor) {
                        editor.on('keydown', function(event) {
                            if (event.keyCode === 27) {
                                const dom = editor.dom;
                                const parentBlock = editor.selection.getSelectedBlocks()[0];

                                if (parentBlock) {
                                    const containerBlock = getObjectPropSafely(() => parentBlock.parentNode.nodeName) === 'BODY' ? dom.getParent(parentBlock, dom.isBlock) : dom.getParent(parentBlock && parentBlock.parentNode, dom.isBlock);
                                    let newBlock = editor.dom.create('p');

                                    newBlock.innerHTML = '<br data-mce-bogus="1">';
                                    dom.insertAfter(newBlock, containerBlock);
                                    let rng = dom.createRng();

                                    newBlock.normalize();
                                    rng.setStart(newBlock, 0);
                                    rng.setEnd(newBlock, 0);
                                    editor.selection.setRng(rng);
                                }
                            }
                            // if (event.keyCode === 37) {
                            //     const dom = editor.dom;
                            //
                            //     const node = editor.selection.getNode();
                            //     const selection = editor.selection.getSel();
                            //     const range = editor.selection.getRng();
                            //
                            //     if (node && node.nodeName === 'BUTTON' && node.className === 'insert-word') {
                            //         // editor.selection.setCursorLocation(node, 1);
                            //         const nextNode = editor.dom.getNext(node, '*');
                            //         const prevNode = editor.dom.getPrev(node, '*');
                            //
                            //         editor.selection.setRng(range, false);
                            //     }
                            //
                            // }
                            //
                            // if (event.keyCode === 39) {
                            //
                            // }
                        });
                        editor.on('click', function(e) {
                            // if (e.target.nodeName === 'SPAN' && e.target.className === 'icon-antsomi-close') {
                            //     e.target.parentElement.parentElement.remove();
                            // }
                            if (e.target.nodeName === 'EM' && e.target.className === 'editor-icon-remove') {
                                e.target.parentElement.remove();
                            }

                            if (e.target.className === 'insert-word' && e.target.id) {
                                editText(e);
                            }
                        });
                    },
                    resize: true,
                    entity_encoding: 'raw',
                    forced_root_block : false,
                    force_br_newlines : true, // tried with and without
                    force_p_newlines : false //
                }}
                // outputFormat='text'
                onEditorChange={handleEditorChange} />
            <div id={'antsomi-popover-customer'} style={{position: 'absolute', top: 45, left: 50}}>
                <Popover
                    placement="bottom-start"
                    isOpen={popoverOpen}
                    target="antsomi-popover-customer"
                    toggle={togglePopover}
                    trigger={'click'}
                    hideArrow={true}
                    className={'antsomi-popover'}
                >
                    <PopoverBody style={{maxWidth: 650, height: 300 ,maxHeight: 450, padding: 0}}>
                        <div className={classnames(styles['antsomi-wrapper-personalization'])}>
                            <div className={classnames(styles['header'])}>
                                <span className={classnames(styles['title'])}> Add Personalization </span>
                            </div>
                            <div className={classnames(styles['body'])}>
                                <table>
                                    <tr>
                                        <th>Personzalization type*</th>
                                        <th>Attribute*</th>
                                        <th>Default Value</th>
                                    </tr>
                                    <tr>
                                        <td>
                                            <DropDown
                                                uniqueKey={'dropDownPersonalType'}
                                                sources={props.personalizationType}
                                                onChange={onChangePersonalType}
                                                label={getObjectPropSafely(() => selectedProperties.personalType.label)}
                                                modifiers={modifiers}
                                            />
                                        </td>
                                        <td>
                                            <DropDown
                                                uniqueKey={'dropDownAttribute'}
                                                sources={sourceAttribute}
                                                onChange={onChangeAttribute}
                                                label={getObjectPropSafely(() => selectedProperties.attribute.label)}
                                                modifiers={modifiers}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className={classnames(styles['input-outline'])}
                                                placeholder={'Enter value'}
                                                value={getObjectPropSafely(() => selectedProperties.value)}
                                                onChange={(event) => onChangeInputValue(event.target.value)}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Personalization code</td>
                                        <td colSpan={2}>
                                            <div style={{wordBreak: 'break-all'}}>
                                                {personalizationCode}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Personalization tag</td>
                                        <td colSpan={2}>
                                            <div style={{wordBreak: 'break-all'}}>
                                                {
                                                    <button className={classnames(styles['insert-word'], styles[classNameTag])}>
                                                        {getObjectPropSafely(() => selectedProperties.attribute.label)}
                                                    </button>
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                            <div className={classnames(styles['footer'])}>
                                <div className={classnames(styles['group-btn'])}>
                                    <button className={classnames(styles['btn'], styles['btn-default'])} onClick={togglePopover}>
                                        Cancel
                                    </button>
                                    <button className={classnames(styles['btn'], styles['btn-blue'])} onClick={handleInsertText}>
                                        Insert
                                    </button>
                                </div>
                            </div>
                        </div>
                    </PopoverBody>
                </Popover>
            </div>
        </div>
    );
};

TinymceEditor.propTypes = {
    onChange: PropTypes.func,
    onError: PropTypes.func,
    htmlText: PropTypes.string,
    personalizationType: PropTypes.array,
    personalizationData: PropTypes.object,
    minHeight: PropTypes.number,
    maxHeight: PropTypes.number,
    maxWidth: PropTypes.number,
    menubar: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    plugins: PropTypes.arrayOf(PropTypes.string),
    toolbar: PropTypes.string
};

TinymceEditor.defaultProps = defaultProps;
